"use client";

import Image from "next/image";
import { useEffect, useRef } from "react";
import type { createNeighborhoodScene } from "./footer-neighborhood-scene";

export function FooterNeighborhood() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let disposed = false;
    let loading = false;
    let visible = false;
    let failed = false;
    let scene: Awaited<ReturnType<typeof createNeighborhoodScene>> | undefined;

    const sync = async () => {
      const active = visible && !document.hidden && !motion.matches;
      if (active && !scene && !loading && !failed) {
        loading = true;
        try {
          const { createNeighborhoodScene } =
            await import("./footer-neighborhood-scene");
          if (disposed) return;
          const created = await createNeighborhoodScene(container, canvas);
          if (disposed) {
            created.dispose();
            return;
          }
          scene = created;
        } catch {
          failed = true;
          // The original artwork remains available when WebGL or loading fails.
        } finally {
          loading = false;
        }
      }
      if (disposed) return;
      scene?.setActive(visible && !document.hidden && !motion.matches);
    };
    const onVisibilityChange = () => {
      void sync();
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
      void sync();
    });
    observer.observe(container);
    motion.addEventListener("change", onVisibilityChange);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      disposed = true;
      observer.disconnect();
      motion.removeEventListener("change", onVisibilityChange);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      scene?.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="group bg-secondary relative h-60 overflow-hidden sm:aspect-[2172/404] sm:h-auto"
    >
      <div aria-hidden="true" className="absolute inset-0">
        <Image
          src="/images/footer-neighborhood-transparent.webp"
          alt=""
          fill
          sizes="100vw"
          className="origin-bottom scale-[1.35] object-cover object-bottom motion-safe:group-data-[animated=true]:invisible sm:hidden"
        />
        <Image
          src="/images/footer-neighborhood-skyline.webp"
          alt=""
          fill
          sizes="100vw"
          className="hidden object-contain object-bottom motion-safe:group-data-[animated=true]:invisible sm:block"
        />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 h-full w-full opacity-0 motion-reduce:invisible"
        />
      </div>
    </div>
  );
}
